import React, { useState, useEffect } from 'react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Table from '../../../components/ui/Table';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Badge from '../../../components/ui/Badge';
import Loading from '../../../components/ui/Loading';
import Alert from '../../../components/ui/Alert';
import { Plus, Edit2, Trash2, Search, Mail, Phone, User, Shield } from 'lucide-react';
import { userManagementService } from '../../../services';
import type { UserManagement, Role, CreateUserRequest, UpdateUserRequest } from '../../../types';

const UsersManagementTab: React.FC = () => {
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<number | undefined>();
  const [showInactive, setShowInactive] = useState(false);
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserManagement | null>(null);
  
  const [formData, setFormData] = useState<Partial<CreateUserRequest & UpdateUserRequest>>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_id: undefined,
    password: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, [selectedRole, showInactive]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [usersResponse, rolesResponse] = await Promise.all([
        userManagementService.getUsers({
          role_id: selectedRole,
          is_active: showInactive ? undefined : true,
          include_child_companies: true,
        }),
        userManagementService.getRoles(),
      ]);
      
      setUsers(usersResponse.users || []);
      setRoles(rolesResponse.roles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.role_id || !formData.password) {
        setError('Please fill in all required fields');
        return;
      }
      
      await userManagementService.createUser(formData as CreateUserRequest);
      setSuccess('User created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await userManagementService.updateUser(selectedUser.profile_id, formData as UpdateUserRequest);
      
      // Update role if changed
      if (formData.role_id && formData.role_id !== selectedUser.role_id) {
        await userManagementService.updateUserRole(selectedUser.profile_id, { role_id: formData.role_id });
      }
      
      setSuccess('User updated successfully');
      setIsEditModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await userManagementService.deleteUser(selectedUser.profile_id);
      setSuccess('User deactivated successfully');
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const openEditModal = (user: UserManagement) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone || '',
      role_id: user.role_id,
      is_active: user.is_active,
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (user: UserManagement) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role_id: undefined,
      password: '',
      is_active: true,
    });
    setSelectedUser(null);
  };

  const filteredUsers = users.filter((user) =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      label: 'User',
      render: (user: UserManagement) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-primary/10 rounded-full flex items-center justify-center">
            <User size={18} className="text-accent-primary" />
          </div>
          <div>
            <div className="font-medium text-text-primary">
              {user.first_name} {user.last_name}
            </div>
            <div className="flex items-center gap-1 text-xs text-text-muted">
              <Mail size={12} />
              {user.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (user: UserManagement) => (
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-text-muted" />
          <span className="text-text-secondary">{user.role_name || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'company',
      label: 'Company',
      render: (user: UserManagement) => (
        <span className="text-text-secondary">{user.company_name || 'N/A'}</span>
      ),
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (user: UserManagement) => (
        <div className="flex items-center gap-1 text-text-secondary">
          {user.phone && <Phone size={14} className="text-text-muted" />}
          <span>{user.phone || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'last_login',
      label: 'Last Login',
      render: (user: UserManagement) => (
        <span className="text-text-secondary text-xs">
          {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (user: UserManagement) => (
        <Badge variant={user.is_active ? 'success' : 'danger'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user: UserManagement) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(user)}
            title="Edit"
          >
            <Edit2 size={16} />
          </Button>
          {user.is_active && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteModal(user)}
              className="text-status-danger hover:bg-status-danger/10"
              title="Deactivate"
            >
              <Trash2 size={16} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {(error || success) && (
        <>
          {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
          {success && <Alert variant="success" message={success} onClose={() => setSuccess(null)} />}
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Users Management</h2>
          <p className="text-text-muted text-sm mt-1">Manage user accounts and roles</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus size={18} className="mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
              <Input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                fullWidth
              />
            </div>
            <Select
              value={selectedRole?.toString() || ''}
              onChange={(e) => setSelectedRole(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-48"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </option>
              ))}
            </Select>
            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
              />
              <span className="text-sm text-text-secondary">Show inactive</span>
            </label>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loading size="lg" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
              <User size={32} className="text-text-muted" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">No users found</h3>
            <p className="text-text-muted mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first user'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus size={18} className="mr-2" />
                Add User
              </Button>
            )}
          </div>
        ) : (
          <Table columns={columns} data={filteredUsers} />
        )}
      </Card>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        title="Add New User"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name *"
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="John"
              required
            />
            <Input
              label="Last Name *"
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Doe"
              required
            />
          </div>
          <Input
            label="Email *"
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john.doe@example.com"
            required
          />
          <Input
            label="Password *"
            type="password"
            value={formData.password || ''}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••"
            required
          />
          <Input
            label="Phone"
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
          <Select
            label="Role *"
            value={formData.role_id?.toString() || ''}
            onChange={(e) => setFormData({ ...formData, role_id: parseInt(e.target.value) })}
            required
          >
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.role_id} value={role.role_id}>
                {role.role_name}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-sm text-text-secondary">Active</span>
          </label>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateUser}>Create User</Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          resetForm();
        }}
        title="Edit User"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name *"
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="John"
              required
            />
            <Input
              label="Last Name *"
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Doe"
              required
            />
          </div>
          <Input
            label="Email *"
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john.doe@example.com"
            required
          />
          <Input
            label="Phone"
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
          <Select
            label="Role *"
            value={formData.role_id?.toString() || ''}
            onChange={(e) => setFormData({ ...formData, role_id: parseInt(e.target.value) })}
            required
          >
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.role_id} value={role.role_id}>
                {role.role_name}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-sm text-text-secondary">Active</span>
          </label>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateUser}>Update User</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        title="Deactivate User"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Are you sure you want to deactivate <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>? 
            They will no longer be able to access the system.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteUser}>
              Deactivate
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersManagementTab;
